import React, { useState, useRef } from 'react';
import { injectIntl } from 'react-intl';
import { cleanFieldProps, i18nIntl } from '../../lib/ep.js';
import DatePicker from 'react-datepicker';

import FieldWrapper from './FieldWrapper.js';
import Input from './Input.js';
import ButtonCollection from './ButtonCollection.js';
import ActionButton, { SquaredButton } from './ActionButton.js';
import FloatingLayer from './FloatingLayer.js';

const DateTimePicker = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);
	const name = props?.name ?? false;
	const db_format = 'YYYY-MM-DD HH:mm:00';
	const american_format = false;

	const ensureDateIsString = (_date) => {
		if(_date == undefined || _date == '' || _date === false) return false;
		if(typeof(_date) == 'string') return _date;
		return _date.format(db_format);
	};

	const ensureDateIsMoment = (_date) => {
		if(_date == undefined || _date == '' || _date === false) return undefined;
		if(typeof(_date) == 'string') return EPDate(_date);
		return _date;
	};

	const standalone = props?.standalone ?? false;	// new input format with CalendarView displayed in a layer
	const show_time = props?.show_time ?? true;
	const date_format = props?.date_format ?? ((american_format) ? 'MM-DD-YYYY' : 'DD-MM-YYYY');
	const time_format = (show_time) ? (props?.time_format ?? 'HH:mm') : '';
	const disabled = props?.disabled ?? false;
	const min_date = ensureDateIsString(props.min_date);
	const max_date = ensureDateIsString(props.max_date);

	/* Receives a date in "YYYY-MM-DD HH:mm:00" format and returns a date in "DD-MM-YYYY HH:mm" or in "MM-DD-YYYY HH:mm" format */
	const dateToHumanFormat = (date_str, display_time = true) => {
		if(date_str == '' || date_str == '0000-00-00 00:00:00') {
			date_str = EPDateFormat(null, db_format);	// now
		}

		if(standalone === false && typeof(date_str) != 'string') {
			date_str = date_str.format(db_format);
		}

		const date_parts = date_str.split(" ");
		if((show_time && display_time) && date_parts.length < 2) return false;	// no time in the value

		const _time_parts = (show_time && display_time) ? date_parts[1].split(":").slice(0, 2) : ['00', '00'];

		const [ year, month, day] = date_parts[0].split("-");
		if(year == undefined || month == undefined || day == undefined) return false;

		const date_loc = (american_format) ? `${month}-${day}-${year}` : `${day}-${month}-${year}`;
		const time_loc = (show_time && display_time) ? ` ${_time_parts.join(":")}` : '';

		return `${date_loc}${time_loc}`;
	};

	/* Receives a date in "DD-MM-YYYY HH:mm" or in "MM-DD-YYYY HH:mm" format and returns a date in "YYYY-MM-DD HH:mm:00" format */
	const dateToDatabaseFormat = (date_str) => {
		if(date_str.indexOf("-") < 0) return false;
		
		const date_parts = date_str.split(" ");
		if(show_time && date_parts.length < 2) return false;	// no time in the value

		const _time_parts = (show_time) ? date_parts[1].split(":").slice(0, 2) : ['00', '00'];

		// american_format: _1 => month | _2 => day
		// NON american_format: _1 => day | _2 => month
		const [ _1, _2, year] = date_parts[0].split("-");
		if(_1 == undefined || _2 == undefined || year == undefined) return false;

		const date_loc = (american_format) ? `${year}-${_1}-${_2}` : `${year}-${_2}-${_1}`;

		return `${date_loc} ${_time_parts.join(":")}:00`;
	};

	const getDefaultValue = () => {
		const prop_name = props?.value ?? ((props.model != undefined && props.model[name] != null) ? props.model[name] : '');
		return ensureDateIsString(prop_name);
	};

	const [ value, setValue ] = useState(()=>getDefaultValue());
	const [ date, setDate ] = useState(dateToHumanFormat(value));
	const [ visible, setVisible ] = useState(false);
	const [ error, setError ] = useState(false);

	const refs = useRef({});

	if(name === false) return null;

	const inputRef = (_ref) => {
		refs.current['element'] = _ref;
		if(props.inputRef !== undefined) props.inputRef(_ref);
	};

	/*
	Chage from the ReactDatePicker component.
	We don't need to validate, since dates can only be picked from
	a specific list of pre-validated by the component options.
	*/
	const change = (v) => {
		if(!standalone) {
			setValue(v);
			setDate(dateToHumanFormat(v.format(db_format)))
			if(props.change !== undefined) props.change(props.name, v);
			return;
		}

		const db_date = EPDateFormat(v, db_format);
		update(db_date);
	};

	/*
	Chage from the input directly.
	Simply update the value. It will be validated on field blur
	to avoid accidental temporally (i.e. while writing) wrong date values.
	*/
	const standaloneChange = (k, v) => {
		setDate(v);
	};

	const validateDate = () => {
		setError(false);

		const db_date = dateToDatabaseFormat(date);

		if(db_date === false || EPDateIsValid(db_date) === false) {
			setError((show_time) ? "The date / time is not valid." : "The date is not valid.");
			return;
		}

		if(min_date !== false && EPDateBefore(db_date, min_date)) {
			setError(props.intl.formatMessage({
				id:"The date can't be previous to {date}."
			}, {
				date:dateToHumanFormat(min_date, false),
			}));
			return;
		}

		if(max_date !== false && EPDateAfter(db_date, max_date)) {
			setError(props.intl.formatMessage({
				id:"The date can't be after to {date}."
			}, {
				date:dateToHumanFormat(max_date, false),
			}));
			return;
		}

		update(db_date);
	};

	const update = (v) => {
		setValue(v);
		setDate(dateToHumanFormat(v));

		if(props.change !== undefined) props.change(props.name, v);
	};

	const field_class = ['common_form_item'];
	if(disabled) field_class.push('disabledField');

	const wrapper_props = cleanFieldProps(props);

	const datepicker_props = {
		name:props.name,
		placeholderText:i18n(props?.placeholder ?? "Date and Time (24h)"),
		locale:(window.is_spanish) ? 'es-es' : 'en-us',
		minTime:props.min_time,
		maxTime:props.max_time,
		selected:ensureDateIsMoment(value),
		onChange:change,
		showTimeSelect:show_time,
		dateFormat:(show_time) ? `${date_format} ${time_format}` : date_format,
		timeFormat:time_format,
		timeCaption:i18n(props?.time_caption ?? "Time"),
		timeIntervals:props?.time_interval ?? 5,
		popperPlacement:props?.position ?? 'bottom-end',
	};

	if(min_date !== false) datepicker_props.minDate = ensureDateIsMoment(min_date);
	if(max_date !== false) datepicker_props.maxDate = ensureDateIsMoment(max_date);

	if(standalone) {
		return (
			<>
				<Input
					errors={(error !== false) ? { [props.name]:i18n(error) } : null}
					{...props}
					change={standaloneChange}
					value={date}
					onBlur={validateDate}
					actions={<ButtonCollection
						_class={{ other:'in_top' }}
					>
						<SquaredButton
							icon='knk-calendar'
							disabled={(error !== false) ? true : false}
							onClick={() => setVisible(true)}
						/>
					</ButtonCollection>}
				/>

				{(visible) && (
					<FloatingLayer
						_class={{ size:'small' }}
						backButton={() => setVisible(false)}
					>
						<div class="full_datetime_picker is_not_padded">
							<DatePicker
								ref={inputRef}
								{...datepicker_props}
								startOpen={true}
								shouldCloseOnSelect={false}
								inline={true}
							/>

							<ButtonCollection
								in_floating={true}
							>
								<ActionButton
									stopPropagation={true}
									onClick={() => setVisible(false)}
								>Confirm</ActionButton>
							</ButtonCollection>
						</div>
					</FloatingLayer>
				)}
			</>
		)
	}

	return (
		<FieldWrapper
			{...wrapper_props}
			type='datetime'
		>
			<DatePicker
				ref={inputRef}
				{...datepicker_props}
				className='common_form_item'
			/>
		</FieldWrapper>
	);
};

export default injectIntl(DateTimePicker);